import {
	Business,
	DashboardOutlined,
	DescriptionOutlined,
	EventNote,
	ExpandMore,
	LocalHospitalOutlined,
	LocalOfferOutlined,
	LocationCityOutlined,
	PeopleAltOutlined,
	RecentActors,
	SettingsOutlined,
} from '@mui/icons-material'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Drawer,
	Hidden,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
} from '@mui/material'

import { useCheckUserRole } from 'hooks/auth/use-cs-user-role'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { routePaths } from 'routes/route-paths'
import UrlBuilder from 'services/util/url-builder'
import styles from './side-nav.module.css'

interface SideNavProps {
	handleDrawerToggle: () => void
	open: boolean
}

interface NavRoute {
	icon: JSX.Element
	name: string
	link: string
	nestedNav?: NestedNav[]
	flag?: boolean
}

interface NestedNav {
	name: string
	link: string
	flag?: boolean
	icon?: JSX.Element
}

const userUrlBuilder: UrlBuilder = new UrlBuilder('authui')
export const drawerWidth = 220
const offWhite = '#eee'

function isRouteActive(pathname: string, link: string): boolean {
	if (!link.startsWith('/')) {
		return false
	}

	return pathname === link || pathname.startsWith(`${link}/`)
}

// Component
const SideNav: React.FC<SideNavProps> = ({ handleDrawerToggle, open }: SideNavProps) => {
	const { transformers15984, und31148_questions_enhancement } = useFlags()
	const navigate = useNavigate()
	const pathname = globalThis.location.pathname
	const userCanViewProviderPortalManagement = useCheckUserRole([
		'Corestream Administrator',
		'Corestream Implementation',
		'Corestream Client Operations',
	])

	const navRoutes: NavRoute[] = [
		{ icon: <LocationCityOutlined />, link: '/organizations', name: 'Organizations' },
		{
			icon: <LocalOfferOutlined />,
			link: '',
			name: 'Discounts',
			nestedNav: [
				{
					link: '/brands-offers',
					name: 'Brands + Offers',
				},
				{
					link: '/category-management',
					name: 'Category Management',
				},
			],
		},
		{
			icon: <LocalHospitalOutlined />,
			link: '',
			name: 'Benefits',
			nestedNav: [
				{
					link: '/benefit-providers',
					name: 'Benefit Providers',
				},
				{
					link: '/product-types',
					name: 'Product Types',
				},
			],
		},
		{
			icon: <EventNote />,
			link: routePaths.openEnrollments.root,
			name: 'Open Enrollments',
		},
		{
			icon: <DescriptionOutlined />,
			link: routePaths.templateQuestions.root,
			name: 'Template Questions',
			flag: und31148_questions_enhancement,
		},
		{
			icon: <SettingsOutlined />,
			link: routePaths.corestreamConfigurations.root,
			name: 'Corestream Configurations',
		},
		{
			icon: <PeopleAltOutlined />,
			link: userUrlBuilder.setEndpoint('/en-GB/users').url(),
			name: 'User Manager',
		},
		{
			icon: <RecentActors />,
			link: routePaths.clientMonitoring.root,
			name: 'Client Monitoring',
			flag: transformers15984,
		},
		{
			icon: <Business />,
			link: routePaths.providerPortalManagement.path,
			name: 'Provider Portal Management',
			flag: userCanViewProviderPortalManagement,
		},
		{
			icon: <DashboardOutlined />,
			link: '',
			name: 'Production Support',
			nestedNav: [
				{
					link: routePaths.productionSupport.root,
					name: 'Dashboard',
				},
				{
					link: routePaths.employeeSearch.path,
					name: 'Employee Search',
				},
				{
					link: routePaths.constituents.search.listing,
					name: 'Constituent Search',
				},
				{
					link: routePaths.invoices.listing,
					name: 'Invoices',
				},
				{
					link: routePaths.refunds.root,
					name: 'Refund Summary',
				},
				{
					link: routePaths.fileMonitoring.root,
					name: 'File Monitoring',
				},
			],
		},
	]

	const drawerContent = (
		<>
			<div style={{ marginTop: 64 }} />
			<List>
				{navRoutes
					.filter((x) => x.flag === undefined || x.flag === true)
					.map((nav) =>
						nav.nestedNav ? (
							<ListItemButton key={nav.name} style={{ padding: 0 }}>
								<Accordion
									style={{
										backgroundColor: nav.nestedNav.some((x) => isRouteActive(pathname, x.link))
											? 'var(--neutral-neutral-80)'
											: 'transparent',

										border: 'none',
										boxShadow: 'none',
										paddingBottom: '0px',
										paddingTop: '0px',
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMore />}
										style={{ paddingLeft: '8px' }}
										data-testid={`nav-${nav.name.toLowerCase().replaceAll(/\s+/g, '-')}`}
									>
										<ListItemIcon>{nav.icon}</ListItemIcon>
										<ListItemText primary={nav.name} />
									</AccordionSummary>
									{nav.nestedNav
										.filter((x) => x.flag === undefined || x.flag === true)
										.map((nestedItem) => (
											<AccordionDetails
												key={nestedItem.name}
												className={styles.nestedButton}
												style={{ padding: '0 8px', margin: '2px 0' }}
											>
												<ListItemButton
													style={{ padding: '4px 8px', margin: '2px 0' }}
													component='a'
													href={nestedItem.link}
													onClick={(e): void => {
														e.preventDefault()
														toggleDrawerOnSelect(nestedItem.link)
													}}
													selected={isRouteActive(pathname, nestedItem.link)}
													data-testid={`nav-${nestedItem.name.toLowerCase().replaceAll(/\s+/g, '-')}`}
												>
													<ListItemIcon>{nestedItem.icon ?? nav.icon}</ListItemIcon>
													<ListItemText primary={nestedItem.name} />
												</ListItemButton>
											</AccordionDetails>
										))}
								</Accordion>
							</ListItemButton>
						) : (
							<ListItemButton
								key={nav.name}
								selected={isRouteActive(pathname, nav.link)}
								component='a'
								href={nav.link}
								onClick={(e): void => {
									e.preventDefault()
									toggleDrawerOnSelect(nav.link)
								}}
								data-testid={`nav-${nav.name.toLowerCase().replaceAll(/\s+/g, '-')}`}
							>
								<ListItemIcon>{nav.icon}</ListItemIcon>
								<ListItemText primary={nav.name} />
							</ListItemButton>
						),
					)}
			</List>
		</>
	)

	function toggleDrawerOnSelect(link: string): void {
		handleDrawerToggle()
		if (link.includes('https')) {
			globalThis.location.assign(link)
		} else {
			navigate(link)
		}
	}

	return (
		<nav aria-label='Admin Navigation' className={styles['side-nav']}>
			<Hidden mdUp implementation='js'>
				<Drawer
					sx={{
						flexShrink: 0,
						width: drawerWidth,
						[`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
						'& .MuiAccordion-root': {
							backgroundColor: offWhite,
							border: 'none',
							boxShadow: 'none',
							marginLeft: '-20px',
							paddingBottom: '0px',
							paddingTop: '0px',
						},
						'& .MuiAccordionSummary-content': { margin: '0px' },
						'& .MuiAccordionDetails-root': { paddingLeft: '20' },
					}}
					variant='temporary'
					open={open}
					onClose={handleDrawerToggle}
					ModalProps={{ keepMounted: true }}
				>
					{drawerContent}
				</Drawer>
			</Hidden>
			<Hidden smDown implementation='css'>
				<Drawer
					sx={{
						flexShrink: 0,
						width: drawerWidth,
						[`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
						'& .MuiAccordion-root': {
							backgroundColor: offWhite,
							border: 'none',
							boxShadow: 'none',
							paddingBottom: '0px',
							paddingTop: '0px',
						},
						'& .MuiAccordionSummary-content': { margin: '0px' },
						'& .MuiAccordionDetails-root': { paddingLeft: '20' },
					}}
					variant='permanent'
					anchor='left'
					open
				>
					{drawerContent}
				</Drawer>
			</Hidden>
		</nav>
	)
}

export default SideNav
